import { toast } from "react-toastify";
import { actions } from "../../src/app/modules/Auth/_redux/authRedux";

let notificationShown = false;

let verifying = null;
let refreshing = null;

// Login, refresh and logout answer 401 for reasons a refresh cannot fix.
const AUTH_URL = /\/v2\/account\/(login|refresh|logout)/;
const TOKEN_ERROR_CODES = ["TOKEN_EXPIRED", "INVALID_TOKEN"];

const expiredLocally = token => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return Boolean(payload.exp) && payload.exp * 1000 <= Date.now();
  } catch (e) {
    return false;
  }
};

// The API also answers 401 for plain permission denials, so only a token the server flagged (or one that has
// visibly run out) is worth a refresh.
const isTokenExpiry = (error, sentToken) => {
  const data = error.response && error.response.data;
  return Boolean(data && TOKEN_ERROR_CODES.includes(data.code)) || (sentToken ? expiredLocally(sentToken) : false);
};

// One refresh at a time: every request that hits an expired token waits for the same promise.
function refreshAccessToken(axios, store) {
  if (!refreshing) {
    refreshing = axios
      .post("/v2/account/refresh", {}, { withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(
        ({ data }) => {
          refreshing = null;
          store.dispatch(actions.tokenRefreshed(data.accessToken));
          return data.accessToken;
        },
        error => {
          refreshing = null;
          throw error;
        }
      );
  }
  return refreshing;
}

function endSession(store, token) {
  if (store.getState().auth.authToken === token) {
    toast.info("Session expired. Please login again");
    store.dispatch(actions.sessionEnded());
  }
}

// Only a refresh the server definitively refused (401) ends the session; offline, rate limits, 403 and 5xx do not.
const refreshRefused = error => Boolean(error && error.response && error.response.status === 401);

// The API also answers 401 for plain permission denials, so a 401 only ends the session when
// /me - which every signed-in account may call - rejects the same token.
function verifySession(axios, store, token) {
  if (verifying) return;
  verifying = axios
    .get("/v2/account/me")
    .then(
      () => {},
      error => {
        if (store.getState().auth.authToken !== token) return undefined;
        // The access token ran out in the meantime: that is a refresh, not the end of the session.
        if (isTokenExpiry(error, token)) {
          return refreshAccessToken(axios, store).then(
            () => {},
            refreshError => {
              if (refreshRefused(refreshError)) endSession(store, token);
            }
          );
        }
        if (error.response && error.response.status === 401) endSession(store, token);
        return undefined;
      }
    )
    .then(() => {
      verifying = null;
    });
}

export default function setupAxios(axios, store) {
  axios.interceptors.response.use(
    response => response,
    error => {
      const status = error.response && error.response.status;
      const config = error.config || {};
      const url = config.url || "";
      const { auth: { authToken } } = store.getState();

      if (status === 401 && authToken && !AUTH_URL.test(url)) {
        const sentHeader = (config.headers && config.headers.Authorization) || "";
        const sentToken = sentHeader.replace(/^Bearer /, "");

        // Expired access token: refresh once (shared), then replay the request once.
        if (!config._retried && isTokenExpiry(error, sentToken || authToken)) {
          config._retried = true;
          const replay = token => {
            config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
            return axios.request(config);
          };
          // A refresh finished while this request was in flight: just replay with the newer token.
          if (sentToken && sentToken !== authToken) return replay(authToken);
          return refreshAccessToken(axios, store).then(replay, refreshError => {
            if (refreshRefused(refreshError)) {
              endSession(store, authToken);
              return Promise.reject(error);
            }
            // Offline or a server error during refresh says nothing about the session: surface it as such.
            return Promise.reject(refreshError);
          });
        }

        if (!/\/v2\/account\/(me|login)/.test(url)) {
          verifySession(axios, store, authToken);
        }
      }
      return Promise.reject(error);
    }
  );

  axios.interceptors.request.use(
    config => {
      const {
        auth: { authToken }
      } = store.getState();

      // The refresh call authenticates with its cookie only; a stale Bearer would just be noise.
      if (authToken && !/\/v2\/account\/refresh/.test(config.url || "")) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      return config;
    },
    error => {
      // if user logged out or session expired
      if (error.response.status == 401) {
        //place your reentry code
        if (!notificationShown) {
          // TODO style success, danger and warning notifications
          toast.info("Session expired. Please login again", {
            autoClose: false
          });
          notificationShown = true;
        }
        store.dispatch(actions.sessionEnded());

        alert("unauthorized");
        window.location.reload();
      }
      return Promise.reject(error);
    }
  );
}
